/**
 * The single mapping from a domain state to a colour. Screens never choose a
 * colour themselves, which is what keeps red reserved for a real fault.
 */
import type {
  AgentStatus,
  BuildStatus,
  CapacityState,
  ConfidenceLevel,
  ConnectionState,
  DeploymentStatus,
  LeaseState,
  ProjectStatus,
  SemanticTone,
  SessionStatus,
  Severity,
  TeamMemberStatus,
  WorkItemStatus,
} from '@dcc/shared';
import {
  AGENT_STATUS_TONE,
  BUILD_STATUS_TONE,
  CAPACITY_STATE_TONE,
  CONFIDENCE_TONE,
  CONNECTION_STATE_TONE,
  DEPLOYMENT_STATUS_TONE,
  LEASE_STATE_TONE,
  PROJECT_STATUS_TONE,
  SESSION_STATUS_TONE,
  SEVERITY_TONE,
  TEAM_MEMBER_STATUS_TONE,
  WORK_ITEM_STATUS_TONE,
  stateLabel,
} from '@dcc/shared';

export type ToneKind =
  | 'project'
  | 'workItem'
  | 'member'
  | 'agent'
  | 'session'
  | 'connection'
  | 'lease'
  | 'capacity'
  | 'build'
  | 'deployment'
  | 'severity'
  | 'confidence';

const MAPS: Record<ToneKind, Record<string, SemanticTone>> = {
  project: PROJECT_STATUS_TONE as Record<ProjectStatus, SemanticTone>,
  workItem: WORK_ITEM_STATUS_TONE as Record<WorkItemStatus, SemanticTone>,
  member: TEAM_MEMBER_STATUS_TONE as Record<TeamMemberStatus, SemanticTone>,
  agent: AGENT_STATUS_TONE as Record<AgentStatus, SemanticTone>,
  session: SESSION_STATUS_TONE as Record<SessionStatus, SemanticTone>,
  connection: CONNECTION_STATE_TONE as Record<ConnectionState, SemanticTone>,
  lease: LEASE_STATE_TONE as Record<LeaseState, SemanticTone>,
  capacity: CAPACITY_STATE_TONE as Record<CapacityState, SemanticTone>,
  build: BUILD_STATUS_TONE as Record<BuildStatus, SemanticTone>,
  deployment: DEPLOYMENT_STATUS_TONE as Record<DeploymentStatus, SemanticTone>,
  severity: SEVERITY_TONE as Record<Severity, SemanticTone>,
  confidence: CONFIDENCE_TONE as Record<ConfidenceLevel, SemanticTone>,
};

export function toneFor(kind: ToneKind, state: string): SemanticTone {
  return MAPS[kind][state] ?? 'neutral';
}

/**
 * A non-colour glyph for every tone, so state never depends on colour alone
 * (WCAG 2.2 and colour-blind operators both need this).
 */
export const TONE_GLYPH: Record<SemanticTone, string> = {
  neutral: '○',
  active: '▶',
  success: '✓',
  attention: '!',
  risk: '▲',
  critical: '✕',
};

export const TONE_WORD: Record<SemanticTone, string> = {
  neutral: 'Neutral',
  active: 'Active',
  success: 'Healthy',
  attention: 'Attention',
  risk: 'Risk',
  critical: 'Critical',
};

export { stateLabel };
